import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { XStack } from 'tamagui';
import { GBAlert, GBButton } from '@greenbackapp/ui';
import { useBillingAccounts } from '../../../hooks/useBillingAccounts';
import { BillingAccount } from '../../../services/billingAccountsService';
import { awaitsFirstBusiness } from '../../../pages/manageSubscriptionModel';
import { BUSINESS_START_PATH, isManageSubscriptionPath } from '../../auth/paywallScope';

// D14 (web-2c): someone who paid for Greenback Business and closed the tab
// before naming the business would otherwise land on the dashboard with no
// way back but a support email. Every page of the app shell says so, from
// the billing-accounts list (the one the sidebar badge already asks for), and
// sends them to /business/start, which names the paid group. Not gated on the
// sale switch: naming sells nothing.
//
// FAILS CLOSED. Nothing is shown until a list for this person has arrived and
// names a group they manage that awaitsFirstBusiness: never while it is
// coming, when it failed, or for anyone else's list.

const BannerWrap = styled.div`
  margin: 16px 24px 0;
`;

export const NAME_YOUR_BUSINESS = 'Name your business';

/** The first group the signed-in person paid for and has not named yet, or null. */
export function useGroupAwaitingFirstBusiness(): BillingAccount | null {
  const { status, data } = useBillingAccounts();
  if (status !== 'ready' || !data) return null;
  return data.accounts.find(awaitsFirstBusiness) ?? null;
}

const isBusinessPath = (pathname: string): boolean =>
  pathname === '/business' || pathname.startsWith('/business/');

/**
 * The banner, on every app-shell page but /business/* (its onboarding and
 * PreviewBanner say it there) and /subscription (the group's own card has
 * the button).
 */
export const PaidUnnamedBusinessBanner: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const group = useGroupAwaitingFirstBusiness();

  if (!group || isBusinessPath(pathname) || isManageSubscriptionPath(pathname)) return null;

  return (
    <BannerWrap>
      <GBAlert
        variant="info"
        title="You've paid for Greenback Business"
        message="Name your business to start keeping its books."
      />
      <XStack marginTop="$2">
        <GBButton variant="primary" size="sm" onPress={() => navigate(BUSINESS_START_PATH)}>
          {NAME_YOUR_BUSINESS}
        </GBButton>
      </XStack>
    </BannerWrap>
  );
};

export default PaidUnnamedBusinessBanner;
